import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { sessionStorage } from '@/utils/sessionStorage'
import { tokenStorage } from '@/services/tokenStorage'
import { UserRole, OrganizationRole } from '@/types/enums'
import { invalidateTimezoneCache } from '@/utils/timezoneUtils'

interface User {
  id: string
  email: string
  name?: string
  role: UserRole
  globalRole?: UserRole
  orgRole?: OrganizationRole
  parentId?: string
  adminId?: string
  currentOrganizationId?: string
  emailVerified?: boolean
  mustChangePassword?: boolean
  hasPasskey?: boolean
  profilePhotoType?: string
  profilePhotoUrl?: string | null
  gravatarEmail?: string | null
  orgProfilePhotoType?: string
  orgProfilePhotoUrl?: string | null
  orgGravatarEmail?: string | null
  timezone?: string | null
  timezonePreference?: 'system' | 'organization' | 'custom'
  distanceUnit?: 'MILES' | 'KILOMETERS'
  temperatureUnit?: 'FAHRENHEIT' | 'CELSIUS'
  availableOrganizations?: Array<{
    id: string
    organizationId: string
    organizationName: string
    organizationSlug: string
    role: OrganizationRole
    subscriptionTier?: string
    unlimitedQuota?: boolean
  }>
}

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  token: string | null
  loading: boolean
  error: string | null
  sessionRestored: boolean
}

// Initialize state with session data if available
const initializeState = (): AuthState => {
  const session = sessionStorage.getSession()

  return {
    isAuthenticated: !!session?.user,
    user: session?.user || null,
    token: session?.token || null,
    loading: false,
    error: null,
    sessionRestored: false
  }
}

const initialState: AuthState = initializeState()

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true
      state.error = null
    },
    loginSuccess: (state, action: PayloadAction<{
      user: User;
      token: string;
      refreshToken?: string;
      expiresIn?: number;
    }>) => {
      state.loading = false
      state.isAuthenticated = true

      // Set orgRole based on current organization membership
      const user = action.payload.user
      if (user.currentOrganizationId && user.availableOrganizations) {
        const currentOrgMembership = user.availableOrganizations.find(
          org => org.organizationId === user.currentOrganizationId
        )
        user.orgRole = currentOrgMembership?.role
      }

      state.user = user
      state.token = action.payload.token
      state.error = null
      state.sessionRestored = true

      // Invalidate timezone cache when user logs in
      invalidateTimezoneCache()

      // Save session to localStorage (for quick user data access on reload)
      sessionStorage.saveSession(action.payload.token, user)

      // Store refresh token in IndexedDB (async, fire and forget)
      if (action.payload.refreshToken) {
        tokenStorage.storeRefreshToken(action.payload.refreshToken).catch(err => {
          console.error('Failed to store refresh token:', err)
        })
      }
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.isAuthenticated = false
      state.user = null
      state.token = null
      state.error = action.payload
      state.sessionRestored = true
    },
    logout: (state) => {
      state.isAuthenticated = false
      state.user = null
      state.token = null
      state.error = null
      state.sessionRestored = true

      // Invalidate timezone cache when user logs out
      invalidateTimezoneCache()

      // Clear session storage
      sessionStorage.clearSession()

      // Clear token storage (async, fire and forget)
      tokenStorage.clearTokens().catch(err => {
        console.error('Failed to clear tokens:', err)
      })
    },
    clearError: (state) => {
      state.error = null
    },
    sessionRestoreStart: (state) => {
      state.loading = true
      state.error = null
    },
    sessionRestoreSuccess: (state, action: PayloadAction<{
      user: User;
      token: string;
      refreshToken?: string;
    }>) => {
      state.loading = false
      state.isAuthenticated = true

      // Set orgRole based on current organization membership
      const user = action.payload.user
      if (user.currentOrganizationId && user.availableOrganizations) {
        const currentOrgMembership = user.availableOrganizations.find(
          org => org.organizationId === user.currentOrganizationId
        )
        user.orgRole = currentOrgMembership?.role
      }

      state.user = user
      state.token = action.payload.token
      state.error = null
      state.sessionRestored = true

      // Invalidate timezone cache when session is restored
      invalidateTimezoneCache()

      // Update session storage with refreshed data
      sessionStorage.saveSession(action.payload.token, user)

      // Store refresh token if provided (from token refresh)
      if (action.payload.refreshToken) {
        tokenStorage.storeRefreshToken(action.payload.refreshToken).catch(err => {
          console.error('Failed to store refresh token:', err)
        })
      }
    },
    sessionRestoreFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.isAuthenticated = false
      state.user = null
      state.token = null
      // "No session found" is a normal state (first visit / admin subdomain), not an error
      state.error = action.payload === 'No session found' ? null : action.payload
      state.sessionRestored = true

      // Clear any stale session data
      sessionStorage.clearSession()
      tokenStorage.clearTokens().catch(() => {})
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }

        // Update orgRole if currentOrganizationId changed
        if (action.payload.currentOrganizationId && state.user.availableOrganizations) {
          const currentOrgMembership = state.user.availableOrganizations.find(
            org => org.organizationId === action.payload.currentOrganizationId
          )
          state.user.orgRole = currentOrgMembership?.role
        }

        // Invalidate timezone cache if timezone-related fields changed
        if (action.payload.timezone !== undefined ||
            action.payload.timezonePreference !== undefined ||
            action.payload.currentOrganizationId !== undefined) {
          invalidateTimezoneCache()
        }

        // Update session storage with new user data
        if (state.token) {
          sessionStorage.saveSession(state.token, state.user)
        }
      }
    },
    updateToken: (state, action: PayloadAction<{
      token: string;
      refreshToken?: string;
    }>) => {
      state.token = action.payload.token

      // Update session storage with new token
      if (state.user) {
        sessionStorage.saveSession(action.payload.token, state.user)
      }

      // Store new refresh token if provided
      if (action.payload.refreshToken) {
        tokenStorage.storeRefreshToken(action.payload.refreshToken).catch(err => {
          console.error('Failed to store refresh token:', err)
        })
      }
    },
    updateOrganization: (state, action: PayloadAction<{ organizationId: string; name: string }>) => {
      // Check availableOrganizations
      if ((state.user as any)?.availableOrganizations) {
        const orgIndex = (state.user as any).availableOrganizations.findIndex(
          (org: any) => org.organizationId === action.payload.organizationId
        );
        if (orgIndex !== -1) {
          (state.user as any).availableOrganizations[orgIndex].organizationName = action.payload.name;
        }
      }
      // Update session storage
      if (state.token && state.user) {
        sessionStorage.saveSession(state.token, state.user);
      }
    }
  },
})

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  clearError,
  sessionRestoreStart,
  sessionRestoreSuccess,
  sessionRestoreFailure,
  updateUser,
  updateToken,
  updateOrganization
} = authSlice.actions

export { authSlice }
export default authSlice.reducer
